/* eslint-disable max-lines */
import { OauthAEMConfig } from './config.interfaces';
import {
  ErrorDetails,
  Errors,
  PingFederateError,
  RequestContext
} from './errors.interfaces';
import { AuthorizeResponse } from './oauth.interfaces';

export interface InitiateSsoSession {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  user?: User;
  showRememberMyUsername?: boolean;
  showThisIsMyDevice?: boolean;
  thisIsMyDeviceSelected?: boolean;
  showCaptcha?: boolean;
  rememberMyUsernameSelected?: boolean;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
}

export interface InitiateAuthenticationParams {
  flowId?: string;
  oauthConfig?: OauthAEMConfig;
  lastNameFormControlValue?: string;
  authId?: string;
  fingerPrint?: string;
  encryptedToken?: string;
}

export interface InitiateAuthentication {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  devices?: Devices[];
  user?: User;
  userMessage?: string;
  trackingId?: string;
  selectedDeviceRef?: SelectedDeviceRef;
  manualPairingPermitted?: boolean;
  manualPairing?: boolean;
  userSelectedDefault?: boolean;
  changeDevicePermitted?: boolean;
  usePasswordAuthenticationEnabled?: boolean;
  manageDevicesAllowed?: boolean;
  newPairingAuthRequired?: boolean;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
}

export interface Devices {
  id?: string;
  type?: string;
  target?: string;
  usable?: boolean;
  nickname?: string;
  defaultDevice?: boolean;
  pushEnabled?: boolean;
  name?: string;
}

export interface User {
  id?: string;
  username?: string;
}

export interface SelectedDeviceRef {
  id: string;
}

export interface SubmitEmailOtp {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
  user?: User;
}

export interface SubmitEmailOtpPayload {
  otp: string;
}

export interface SubmitEmailPayload {
  email: string;
}

export interface CancelAuthentication {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  user?: User;
  authorizeResponse?: AuthorizeResponse;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
}

export type DeviceType = 'REGISTER' | 'Email' | 'SMS' | 'MOBILE';

export type OperationTypes =
  | 'INITIATE_RESET_PASSWORD'
  | 'SUBMIT_SKYMILES_NUMBER'
  | 'INITIATE_DEVICE_METHOD'
  | 'SELECT_DEVICE_METHOD'
  | 'SUBMIT_OTP'
  | 'SUBMIT_NEW_PASSWORD'
  | 'INITIATE_FLY_DELTA_POLL';

export type InitiateSsoOperationTypes =
  | 'NEW_USER_WITH_VALIDATION'
  | 'NEW_USER_WITHOUT_VALIDATION'
  | 'SELF_SERVICE_RESET_PASSWORD'
  | 'CHANGE_PASSWORD'
  | 'DEFAULT';

export interface DevicePairingMethods {
  deviceType?: DeviceType;
  applicationName?: string;
  applicationId?: string;
}

export interface MfaLinks {
  authenticate?: { href: string };
  activateSmsDevice?: { href: string };
  self?: { href: string };
  cancelDevicePairing?: { href: string };
  selectDevicePairingMethod?: { href: string };
  submitSmsTarget?: { href: string };
  poll?: { href: string };
  cancelAuthentication?: { href: string };
  initiateAccountRecovery?: { href: string };
  initiatePasswordChange?: { href: string };
  checkUsernamePassword?: { href: string };
  initiateRegistration?: { href: string };
  setDefaultDevice?: { href: string };
  selectDevice?: { href: string };
  setupMfa?: { href: string };
  checkOtp?: { href: string };
  removeDevice?: { href: string };
  cancelRemoveDevice?: { href: string };
  continueAuthentication?: { href: string };
  resendOtp?: { href: string };
  cancel?: { href: string };
  registerUser?: { href: string };
  checkAccountRecoveryUsername?: { href: string };
  cancelAccountRecovery?: { href: string };
  usePasswordAuthentication?: { href: string };
  updateDeviceNickname?: { href: string };
  checkPasswordReset?: { href: string };
  activateEmailDevice?: { href: string };
  checkNewPassword?: { href: string };
  cancelPasswordChange?: { href: string };
  submitIdentifier?: { href: string };
  clearIdentifier?: { href: string };
}

export interface AddMfaBackup {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  devicePairingMethods?: DevicePairingMethods[];
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
}

export interface SubmitSmsMethod {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  _links?: MfaLinks;
  requestContext?: RequestContext;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
}

export interface ContinueAuthentication {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  showCaptcha?: boolean;
  user?: User;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
}

export interface ContinueAccountRecovery {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  showCaptcha?: boolean;
  user?: User;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
}

export interface SubmitFlyDeltaMethod {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  requestContext?: RequestContext;
  applicationName?: string;
  pairingKey?: string;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
}

export interface SubmitSmsNumber {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  phone?: string;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
}

export interface SubmitSmsOtp {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  authorizeResponse?: AuthorizeResponse;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
}

export interface SubmitNumberPayload {
  phone: string;
}

export interface SubmitActivationPayload {
  otp: string;
}

export interface SkymilesNumberPayload {
  username: string;
}

export interface InitiateUserRegistration {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  showCaptcha?: boolean;
  showThisIsMyDevice?: boolean;
  fields?: Fields[];
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
}

export interface MethodSelectionPayload {
  devicePairingMethod: DevicePairingMethods;
}

export interface Fields {
  id: string;
  label: string;
  type: string;
  defaultValue?: string;
  required: boolean;
  readonly: boolean;
}

export interface SubmitUserRegistration {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  user?: User;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
}

export interface SubmitUserRegistrationPayload {
  password: string;
  fieldValues: UserRegistrationInfo;
}

export interface UserRegistrationInfo {
  firstName: string;
  lastName: string;
  email: string;
  userName: string;
  primarySkymilesNumber: string;
}

export interface FlyDeltaPoll {
  id?: string;
  plugTypeId?: string;
  status?: string;
  authorizeResponse?: AuthorizeResponse;
  requestContext?: RequestContext;
  applicationName?: string;
  pairingKey?: string;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
}

export interface CancelPairing {
  id?: string;
  pluginTypeId?: string;
  status: string;
  requestContext?: RequestContext;
  authorizeResponse?: AuthorizeResponse;
  user: User;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
}

export interface InitiateAccountRecovery {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  showCaptcha?: boolean;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
}

export interface SubmitSkymilesNumber {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  user?: { username: string };
  userMessage?: string;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
  details?: ErrorDetails[];
}

export interface Authenticate {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  devices?: Devices[];
  user?: User;
  selectedDeviceRef?: SelectedDeviceRef;
  manualPairingPermitted?: boolean;
  manualPairing?: boolean;
  newPairingAuthRequired?: boolean;
  usePasswordAuthenticationEnabled?: boolean;
  userSelectedDefault?: boolean;
  changeDevicePermitted?: boolean;
  manageDevicesAllowed?: boolean;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
}

export interface SelectDevice {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  devices?: Devices[];
  user?: User;
  selectedDeviceRef?: SelectedDeviceRef;
  manualPairingPermitted?: boolean;
  manualPairing?: boolean;
  userSelectedDefault?: boolean;
  changeDevicePermitted?: boolean;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
}

export interface SelectDevicePayload {
  deviceRef: SelectedDeviceRef;
}

export interface SubmitNewPassword {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
  userMessage?: string;
  details?: ErrorDetails[];
}

export interface SubmitNewPasswordPayload {
  newPassword: string;
}

export interface SubmitEmailResponse {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  email?: string;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
}

export interface ChangeUserPasswordInfo {
  username: string;
  existingPassword: string;
  newPassword: string;
}

export interface InitiatePasswordChange {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  showCaptcha?: boolean;
  existingPasswordRequired?: boolean;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
}

export interface CheckNewPassword {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  userMessage?: string;
  message?: string;
  details?: ErrorDetails[];
}

export interface MfaEnrollment {
  flowId: string;
  load: boolean;
}

export interface DefaultDevice {
  id?: string;
  pluginTypeId?: string;
  status?: string;
  phone?: string;
  _links?: MfaLinks;
  code?: string;
  error?: PingFederateError;
  errors?: Errors[];
  error_description?: string;
  message?: string;
}
